import { useState, useEffect } from "react";
import {
  Zap,
  Play,
  Shield,
  Clock,
  Wallet,
  Globe,
  Zap as FastForward,
  Clock as InstallClock,
  Trophy,
  Tv,
  Gift,
  Monitor,
  Diamond,
} from "lucide-react";
import trustpilot from "../assets/logo/trust-pilot1.svg";
import eredivisie from "../assets/bg/eredivisie.jpg";
import ucl from "../assets/bg/ucl.jpg";
import cover from "../assets/bg/cover.jpg";

function getNextMidnight() {
  const now = new Date();
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return next.getTime();
}

function getRemainingParts(expiresAt) {
  const diff = Math.max(0, expiresAt - Date.now());
  const totalSeconds = Math.floor(diff / 1000);
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    diff,
  };
}

function ColonBlink() {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setInterval(() => setVisible((v) => !v), 500);
    return () => clearInterval(t);
  }, []);
  return (
    <span style={{
      fontSize: "28px", fontWeight: 900,
      color: "#ffffff",
      opacity: visible ? 1 : 0.2,
      transition: "opacity 0.15s ease", userSelect: "none",
      lineHeight: 1, marginBottom: "18px",
    }}>:</span>
  );
}

function TimeBlock({ value, unit }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: "6px",
    }}>
      <div style={{
        minWidth: "64px", padding: "10px 8px",
        background: "rgba(0,0,0,0.55)",
        borderRadius: "10px",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <span style={{
          fontSize: "32px", fontWeight: 900, fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em", color: "#ffffffff", lineHeight: 1,
        }}>{value}</span>
      </div>
      <span style={{
        fontSize: "9px", fontWeight: 600, letterSpacing: "0.15em",
        textTransform: "uppercase", color: "rgba(255, 255, 255, 1)",
      }}>{unit}</span>
    </div>
  );
}

function CountdownTimer() {
  const [expiry, setExpiry] = useState(getNextMidnight());
  const [time, setTime] = useState(() => getRemainingParts(expiry));

  useEffect(() => {
    const tick = () => {
      const parts = getRemainingParts(expiry);
      if (parts.diff <= 0) {
        const next = getNextMidnight();
        setExpiry(next);
        setTime(getRemainingParts(next));
      } else {
        setTime(parts);
      }
    };
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiry]);

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: "10px",
      marginTop: "16px",
    }}>
      <p style={{
        margin: 0, fontSize: "9px", fontWeight: 700,
        letterSpacing: "0.22em", textTransform: "uppercase",
        color: "rgba(255, 255, 255, 1)",
      }}>Aanbieding verloopt in</p>

      <div style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
        <TimeBlock value={pad(time.hours)} unit="uren" />
        <ColonBlink />
        <TimeBlock value={pad(time.minutes)} unit="min" />
        <ColonBlink />
        <TimeBlock value={pad(time.seconds)} unit="sec" />
      </div>

      <p style={{
        margin: 0, fontSize: "11px", fontWeight: 600,
        color: "rgba(255, 255, 255, 1)", textAlign: "center", lineHeight: 1.4,
      }}>Bestel vandaag voor de start van het seizoen. Beperkt aanbod.</p>
    </div>
  );
}

function scrollToPricing() {
  const element = document.getElementById("pricing");
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
}

export default function Home() {
  return (
    <>
      <section className="home-section bg-gradient-to-br bg-slate-50 animated-bg">
        <div className="main-content w-[90%] sm:w-[90%] md:w-[85%] m-auto pt-4 sm:pt-5 lg:pt-0 pb-5">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            {/* Left content */}
            <div className="lg:col-span-7 space-y-4 lg:space-y-8">
              {/* Trustpilot floating badge */}
              <img
                src={trustpilot}
                alt="trustpilot"
                className="w-52 sm:w-64 h-auto -mb-2"
              />

              {/* Main heading with modern typography */}
              <div className="space-y-2 lg:space-y-4">
                <h1 className="text-[2.25rem] sm:text-5xl md:text-6xl lg:text-[3.5rem] xl:text-[4.5rem] font-black text-gray-900 leading-[1.1] tracking-tight main-title">
                  Welkom bij{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff]">
                    FEESTTV
                  </span>
                  <br />
                  De Beste IPTV in
                  <br />
                  Nederland &amp;
                  <br />
                  België
                </h1>
                <div className="text-base sm:text-lg font-bold">
                  <span className="text-xl">
                    <span className="footer-logo flex items-center gap-2">
                      {/* Dutch Flag SVG */}
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-8 rounded shadow-sm border border-gray-200" viewBox="0 0 640 480" role="img" aria-label="Nederland">
                        <path fill="#AE1C28" d="M0 0h640v160H0z"/>
                        <path fill="#FFF" d="M0 160h640v160H0z"/>
                        <path fill="#21468B" d="M0 320h640v160H0z"/>
                      </svg>
                      <span className="text-gray-500 font-black">&amp;</span>
                      {/* Belgian Flag SVG */}
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-8 rounded shadow-sm border border-gray-200" viewBox="0 0 640 480" role="img" aria-label="België">
                        <path fill="#000" d="M0 0h213.3v480H0z"/>
                        <path fill="#FFD90C" d="M213.3 0h213.4v480H213.3z"/>
                        <path fill="#F31830" d="M426.7 0H640v480H426.7z"/>
                      </svg>
                    </span>
                  </span>
                </div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-700 leading-snug">
                  Premium entertainment voor{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff]">
                    al jouw apparaten
                  </span>
                </h2>
              </div>

              {/* Stats cards */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center border border-white/20 shadow-lg">
                  <div className="text-lg sm:text-2xl font-black text-[#04003b]">
                    79K+
                  </div>
                  <div className="text-xs text-gray-600 font-medium">
                    Zenders
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center border border-white/20 shadow-lg">
                  <div className="text-lg sm:text-2xl font-black text-[#090979]">
                    249K+
                  </div>
                  <div className="text-xs text-gray-600 font-medium">VOD</div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center border border-white/20 shadow-lg">
                  <div className="text-lg sm:text-2xl font-black text-[#00d4ff]">
                    4K
                  </div>
                  <div className="text-xs text-gray-600 font-medium">
                    Kwaliteit
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 lg:gap-1">
                <button
                  onClick={scrollToPricing}
                  className="bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] hover:brightness-110 text-white px-5 sm:px-6 lg:px-3 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-500 flex items-center justify-center gap-2 lg:gap-1 min-h-[48px] whitespace-nowrap"
                >
                  <Zap className="w-4 h-4 sm:w-5 sm:h-5 lg:w-4 lg:h-4 flex-shrink-0" />
                  <span className="text-sm sm:text-base">
                    Directe Activering
                  </span>
                </button>
                <a
                  href="#pricing"
                  className="bg-white/90 backdrop-blur-sm hover:bg-white text-gray-900 border border-gray-200/50 px-5 sm:px-6 lg:px-3 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all min-h-[48px] flex items-center justify-center whitespace-nowrap"
                >
                  <span className="text-sm sm:text-base">
                    Bestellen vanaf €49 / 15 mnd
                  </span>
                </a>

                {/* Officiële server-badge — zelfde vormgeving én animatie als 'Directe Activering' */}
                <button
                  type="button"
                  onClick={scrollToPricing}
                  className="w-full sm:w-auto bg-gradient-to-r from-[#04003b] via-[#090979] to-[#00d4ff] hover:brightness-110 text-white px-5 sm:px-6 lg:px-3 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-500 flex items-center justify-center gap-2 lg:gap-1 min-h-[48px] text-center whitespace-nowrap"
                >
                  <Diamond className="w-4 h-4 sm:w-5 sm:h-5 lg:w-4 lg:h-4 flex-shrink-0" />
                  <span className="text-sm sm:text-base">
                    Officiële server ·{" "}
                    <span className="font-black">Diamond IPTV</span>
                  </span>
                </button>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-4 lg:space-y-6">
              <div className="relative">
                {/* Main feature card */}
                <div className="bg-gradient-to-br from-white to-blue-50/50 border-0 shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden">
                  <div className="p-6 sm:p-8">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#04003b] via-[#090979] to-[#00d4ff] rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg">
                      <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white fill-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                      Streaming Premium
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">
                      Geniet van 4K-beeldkwaliteit op elk apparaat met 100%
                      beschikbaarheid en gratis activering van IBO Pro Player.
                    </p>
                    <button 
                        onClick={() => {
                          const element = document.getElementById("pricing");
                          if (element) {
                              element.scrollIntoView({ behavior: "smooth" });
                          }
                        }}
                     className="w-full bg-[#090979] hover:bg-[#0a0a61] text-white rounded-lg sm:rounded-xl font-semibold shadow-lg py-2.5 sm:py-3 transition-colors text-sm sm:text-base min-h-[44px]">
                      Bekijk Alle Pakketten
                    </button>
                  </div>
                </div>

                {/* Floating mini cards */}
                <div className="absolute -right-2 sm:-right-4 -top-2 sm:-top-4 bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl border border-white/20">
                  <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-[#00d4ff] mb-1 sm:mb-2" />
                  <div className="text-xs font-bold text-gray-900">
                    Restitutie
                  </div>
                  <div className="text-xs text-gray-600">Garantie</div>
                </div>

                <div className="absolute -left-2 sm:-left-4 -bottom-[10%] sm:-bottom-[15%] bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl border border-white/20">
                  <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-[#090979] mb-1 sm:mb-2" />
                  <div className="text-xs font-bold text-gray-900">0.5s</div>
                  <div className="text-xs text-gray-600">
                    Laadtijd
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 sm:mt-16 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-4 mb-8 sm:mb-10">
            {/* Garantie de Remboursement */}
            <div className="group hover:shadow-2xl hover:brightness-110 hover:scale-105 transition-all duration-300 bg-gradient-to-r from-[#04003b] to-[#00d4ff] backdrop-blur-sm border-0 shadow-xl rounded-2xl sm:rounded-3xl overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-center lg:justify-start mb-4 sm:mb-6">
                  <Wallet className="w-6 h-6 sm:w-8 sm:h-8 text-slate-50" />
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-50 mb-3 sm:mb-4 text-center lg:text-left">
                  15 Dagen Geld-Terug-Garantie
                </h3>
                <p className="text-slate-100 leading-relaxed text-center lg:text-left text-sm sm:text-base">
                  Niet helemaal tevreden met je IPTV-abonnement? Geen
                  zorgen! Wij bieden 15 dagen niet-goed-geld-terug garantie voor
                  een volledige terugbetaling.
                </p>
              </div>
            </div>

            {/* 79.000+ Zenders */}
            <div className="group hover:shadow-2xl hover:brightness-110 hover:scale-105 transition-all duration-300 bg-gradient-to-r from-[#04003b] to-[#00d4ff] backdrop-blur-sm border-0 shadow-xl rounded-2xl sm:rounded-3xl overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-center lg:justify-start mb-4 sm:mb-6">
                  <Globe className="w-6 h-6 sm:w-8 sm:h-8 text-slate-50" />
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-50 mb-3 sm:mb-4 text-center lg:text-left">
                  79.000+ Zenders Wereldwijd
                </h3>
                <p className="text-slate-100 leading-relaxed text-center lg:text-left text-sm sm:text-base">
                  Toegang tot meer dan 79.000 live TV-zenders en 249.000 films en series,
                  inclusief alle Nederlandse, Belgische en Europese kanalen.
                </p>
              </div>
            </div>

            {/* Connexion Ultra Rapide */}
            <div className="group hover:shadow-2xl hover:brightness-110 hover:scale-105 transition-all duration-300 bg-gradient-to-r from-[#04003b] to-[#00d4ff] backdrop-blur-sm border-0 shadow-xl rounded-2xl sm:rounded-3xl overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-center lg:justify-start mb-4 sm:mb-6">
                  <FastForward className="w-6 h-6 sm:w-8 sm:h-8 text-slate-50" />
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-50 mb-3 sm:mb-4 text-center lg:text-left">
                  Ultra Snelle Verbinding
                </h3>
                <p className="text-slate-100 leading-relaxed text-center lg:text-left text-sm sm:text-base">
                  feesttv maakt gebruik van eigen Europese servers van topkwaliteit,
                  waardoor zenders in 0,5 seconden laden - zonder haperingen of buffering.
                </p>
              </div>
            </div>

            {/* Installation Immédiate */}
            <div className="group hover:shadow-2xl hover:scale-105 transition-all duration-300 bg-gradient-to-r from-[#04003b] to-[#00d4ff] backdrop-blur-sm border-0 shadow-xl rounded-2xl sm:rounded-3xl overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-center lg:justify-start mb-4 sm:mb-6">
                  <InstallClock className="w-6 h-6 sm:w-8 sm:h-8 text-slate-50" />
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-50 mb-3 sm:mb-4 text-center lg:text-left">
                  Directe Installatie
                </h3>
                <p className="text-slate-100 leading-relaxed text-center lg:text-left text-sm sm:text-base">
                  Installeer je IPTV-abonnement eenvoudig op elk apparaat.
                  Wij bieden gratis hulp bij installatie en activering van IBO Pro Player.
                </p>
              </div>
            </div>
          </div>

          {/* Eredivisie & Champions League — Sportbanner */}
          <div className="mb-8 sm:mb-12">
            <div className="sport-banner relative rounded-[2rem] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] max-w-5xl m-auto border border-white/10 group flex items-center bg-gradient-to-br from-[#04003b] via-[#0a1046] to-[#02010f]">
              <span className="sport-banner__beams" aria-hidden="true"></span>
              <span className="sport-banner__pitch" aria-hidden="true"></span>
              <span className="sport-banner__sheen" aria-hidden="true"></span>
              <div className="sport-banner__glow absolute -right-24 -top-24 w-72 h-72 bg-[#00d4ff]/20 rounded-full blur-3xl pointer-events-none z-0"></div>
              <div
                className="sport-banner__glow absolute -left-16 -bottom-24 w-64 h-64 bg-[#090979]/40 rounded-full blur-3xl pointer-events-none z-0"
                style={{ animationDelay: "2.5s" }}
              ></div>

              <div className="relative z-10 w-full px-6 sm:px-8 py-6 flex flex-col md:flex-row items-center text-center md:text-left justify-between gap-4">
                <div className="flex-1 space-y-2 max-w-xl">
                  <div className="inline-flex items-center gap-2 bg-white/10 border border-[#00d4ff]/30 rounded-full px-3 py-1.5 backdrop-blur-md shadow-xl">
                    <Trophy className="w-3.5 h-3.5 text-[#00d4ff] shrink-0" />
                    <span className="text-white text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase">
                      Eredivisie • UEFA Champions League • Seizoen 2026/27
                    </span>
                  </div>

                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-[1.15] drop-shadow-2xl">
                    Het beste van het Nederlandse voetbal <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00d4ff] to-[#8be9ff] drop-shadow-[0_2px_12px_rgba(0,212,255,0.45)]">
                      live en in 4K
                    </span>
                  </h2>

                  <div className="flex lg:hidden justify-center items-end gap-3 py-2">
                    <div className="w-20 h-28 rounded-xl overflow-hidden shadow-xl transform -rotate-6 border border-[#00d4ff]/30">
                      <img src={eredivisie} alt="Eredivisie" className="w-full h-full object-cover" />
                    </div>
                    <div className="w-20 h-28 rounded-xl overflow-hidden shadow-xl border-2 border-[#00d4ff]/60">
                      <img src={cover} alt="Topwedstrijd" className="w-full h-full object-cover" />
                    </div>
                    <div className="w-20 h-28 rounded-xl overflow-hidden shadow-xl transform rotate-6 border border-[#00d4ff]/30">
                      <img src={ucl} alt="UEFA Champions League" className="w-full h-full object-cover" />
                    </div>
                  </div>

                  <p className="text-gray-200 text-xs sm:text-sm font-medium leading-relaxed drop-shadow-md max-w-md mx-auto md:mx-0">
                    Eredivisie, Champions League en de Europese topwedstrijden, elke speelronde,
                    zonder onderbrekingen en op al je apparaten.
                  </p>

                  <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-4">
                    <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 backdrop-blur-md">
                      <Tv className="w-3.5 h-3.5 text-[#00d4ff]" />
                      <span className="text-white text-[10px] sm:text-xs font-semibold">Alle clubs</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 backdrop-blur-md">
                      <Monitor className="w-3.5 h-3.5 text-[#00d4ff]" />
                      <span className="text-white text-[10px] sm:text-xs font-semibold">Alle apparaten</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 backdrop-blur-md">
                      <Zap className="w-3.5 h-3.5 text-[#00d4ff]" />
                      <span className="text-white text-[10px] sm:text-xs font-semibold">Activering in 1 min</span>
                    </div>
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row items-center gap-3">
                    <button
                      onClick={scrollToPricing}
                      className="bg-gradient-to-r from-[#00d4ff] to-[#090979] hover:brightness-110 text-white font-black py-2.5 px-6 rounded-full shadow-lg hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 text-xs sm:text-sm uppercase tracking-widest w-full sm:w-auto"
                    >
                      <Gift className="w-4 h-4" />
                      Nu Reserveren
                    </button>
                    <span className="text-gray-300 text-[10px] sm:text-xs font-medium bg-black/30 px-3 py-1.5 rounded-full backdrop-blur-sm border border-white/10">
                      24/7 support • 100% Veilig betalen
                    </span>
                  </div>

                  <div className="block lg:hidden w-full flex justify-center mt-2">
                    <CountdownTimer />
                  </div>
                </div>

                <div className="relative w-full md:w-auto flex justify-center md:justify-end mt-6 md:mt-0 hidden lg:flex items-center">
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div className="relative w-[320px] h-[184px] group-hover:scale-105 transition-transform duration-700 flex justify-center items-center">
                      <div className="absolute left-0 w-28 h-36 rounded-xl overflow-hidden shadow-2xl transform -rotate-12 -translate-x-3 translate-y-3 group-hover:-rotate-6 transition-all duration-500 z-10 border border-[#00d4ff]/30">
                        <img src={eredivisie} alt="Eredivisie" className="w-full h-full object-cover" />
                      </div>

                      <div className="absolute right-0 w-28 h-36 rounded-xl overflow-hidden shadow-2xl transform rotate-12 translate-x-3 translate-y-3 group-hover:rotate-6 transition-all duration-500 z-10 border border-[#00d4ff]/30">
                        <img src={ucl} alt="UEFA Champions League" className="w-full h-full object-cover" />
                      </div>

                      <div className="absolute z-20 w-32 h-44 rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(0,212,255,0.35)] transform group-hover:-translate-y-2 transition-all duration-500 border-2 border-[#00d4ff]/70">
                        <img src={cover} alt="Topwedstrijd" className="w-full h-full object-cover" />
                      </div>
                    </div>

                    <CountdownTimer />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
